import React, {useEffect} from 'react';
import Image from 'next/image';
import {Typography, Stack, Button, Link, Grid, Box} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import FaxOutlinedIcon from '@mui/icons-material/FaxOutlined';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import {useStyles} from './ohidLogin.styles';

const SESSION_STORAGE_KEYS_TO_CLEAR = [
  'ms-session-id',
  'loginType',
  'user-secure-access',
  'user-msid',
  'user-display-name-msid'
];

interface OhidLoginPageContentTypes {
  authType: string | null;
  msidAuthType: string;
  ohidLoginType: string;
  msidLoginType: string;
  domainName: string;
  loginContent:
    | {
        homeBanner?: string;
        title?: string;
        imgBannerAltText?: string;
        description1?: string;
        link1?: string;
        description2?: string;
        employeeLoginLabel?: string;
        ohidUserLogin?: string;
        showOhidBtn?: string;
        description3?: string;
        ohidPage?: {
          bannerImage?: string;
          buttonText?: string;
          heading?: string;
          subHeading1?: string;
          subHeading2?: string;
          subHeading3?: string;
          emailLabel?: string;
          emailAddress?: string;
          emailImg?: string;
          telephoneLabel?: string;
          telephoneValue?: string;
          telephoneImg?: string;
          faxLabel?: string;
          faxValue?: string;
          faxImg?: string;
          noteLabel?: string;
          noteTextOneValue?: string;
          noteTextTwoValue?: string;
          signInPrompt?: string;
          sectionLabel?: string;
        };
        documents?: {
          title?: string;
          links?: {
            isActive?: boolean;
            label?: string;
            url?: string;
          }[];
        };
      }
    | undefined;
}
export function OhidLoginPage({loginContent, ohidLoginType, domainName}: OhidLoginPageContentTypes) {
  const classes = useStyles();

  useEffect(() => {
    SESSION_STORAGE_KEYS_TO_CLEAR.forEach((key) => {
      sessionStorage.removeItem(key);
    });
  }, []);

  const handleLogin = () => {
    sessionStorage.setItem('loginType', ohidLoginType);
    sessionStorage.removeItem('session');
    window.location.href = `/api/auth/login/ohid?domain=${domainName}`;
  };

  return (
    <Stack className={classes.wrapperBox} component="main" aria-labelledby="ohid-login-heading">
      <Grid className={classes.homeContainer}>
        <Box className={classes.homeContent}>
          <Typography id="ohid-login-heading" variant="h1" component="h1" className={classes.heading1}>
            {loginContent?.ohidPage?.heading}
          </Typography>

          <Typography variant="body1" component="p" className={classes.description}>
            {loginContent?.ohidPage?.subHeading1}
            <strong>{loginContent?.ohidPage?.subHeading2}</strong>
            <br />
            {loginContent?.ohidPage?.subHeading3}
          </Typography>

          {(loginContent?.ohidPage?.telephoneLabel || loginContent?.ohidPage?.telephoneValue) && (
            <Typography variant="body1" component="p" className={classes.emailRow}>
              {loginContent?.ohidPage?.telephoneImg ? (
                <img src={loginContent.ohidPage.telephoneImg} aria-hidden="true" width={20} height={20} alt="" />
              ) : (
                <LocalPhoneOutlinedIcon aria-hidden="true" fontSize="small" sx={{color: '#002677'}} />
              )}
              {loginContent?.ohidPage?.telephoneLabel && <strong>{loginContent.ohidPage.telephoneLabel}</strong>}
              {loginContent?.ohidPage?.telephoneValue && (
                <>
                  {' '}
                  <Link
                    href={`tel:${loginContent.ohidPage.telephoneValue}`}
                    className={classes.emailLink}
                    aria-label={`Telephone ${loginContent.ohidPage.telephoneValue}`}
                  >
                    {loginContent.ohidPage.telephoneValue}
                  </Link>
                </>
              )}
            </Typography>
          )}

          {(loginContent?.ohidPage?.faxLabel || loginContent?.ohidPage?.faxValue) && (
            <Typography variant="body1" component="p" className={classes.emailRow}>
              {loginContent?.ohidPage?.faxImg ? (
                <img src={loginContent.ohidPage.faxImg} aria-hidden="true" width={20} height={20} alt="" />
              ) : (
                <FaxOutlinedIcon aria-hidden="true" fontSize="small" sx={{color: '#002677'}} />
              )}
              {loginContent?.ohidPage?.faxLabel && <strong>{loginContent.ohidPage.faxLabel}</strong>}
              {loginContent?.ohidPage?.faxValue && (
                <>
                  {' '}
                  <Link
                    href={`tel:${loginContent.ohidPage.faxValue}`}
                    className={classes.emailLink}
                    aria-label={`Fax ${loginContent.ohidPage.faxValue}`}
                  >
                    {loginContent.ohidPage.faxValue}
                  </Link>
                </>
              )}
            </Typography>
          )}

          {(loginContent?.ohidPage?.emailLabel || loginContent?.ohidPage?.emailAddress) && (
            <Typography variant="body1" component="p" className={classes.emailRow}>
              {loginContent?.ohidPage?.emailImg ? (
                <img src={loginContent.ohidPage.emailImg} aria-hidden="true" width={20} height={20} alt="" />
              ) : (
                <EmailOutlinedIcon aria-hidden="true" fontSize="small" sx={{color: '#002677'}} />
              )}
              {loginContent?.ohidPage?.emailLabel && <strong>{loginContent.ohidPage.emailLabel}:</strong>}
              {loginContent?.ohidPage?.emailAddress && (
                <>
                  {' '}
                  <Link
                    href={`mailto:${loginContent.ohidPage.emailAddress}`}
                    className={classes.emailLink}
                    aria-label={`Email ${loginContent.ohidPage.emailAddress}`}
                  >
                    {loginContent.ohidPage.emailAddress}
                  </Link>
                </>
              )}
            </Typography>
          )}

          {(loginContent?.ohidPage?.noteLabel || loginContent?.ohidPage?.noteTextOneValue) && (
            <Typography variant="body2" component="p" className={classes.note}>
              {loginContent?.ohidPage?.noteLabel && <strong>{loginContent.ohidPage.noteLabel}:</strong>}{' '}
              {loginContent?.ohidPage?.noteTextOneValue}
              <strong>{loginContent?.ohidPage?.noteTextTwoValue}</strong>
            </Typography>
          )}

          <Typography variant="body1" component="p" className={classes.signInPrompt}>
            {loginContent?.ohidPage?.signInPrompt}
          </Typography>

          <Button
            variant="contained"
            className={classes.signBtn}
            onClick={handleLogin}
            endIcon={<ArrowForwardIcon fontSize="small" />}
            aria-label={loginContent?.ohidPage?.buttonText}
          >
            {loginContent?.ohidPage?.buttonText || loginContent?.ohidUserLogin}
          </Button>
        </Box>
        {loginContent?.ohidPage?.bannerImage && (
          <Image
            src={loginContent.ohidPage.bannerImage}
            alt={loginContent?.imgBannerAltText ?? ''}
            width={450}
            height={400}
            className={classes.bgOverlayImage}
            unoptimized
            priority={false}
          />
        )}
      </Grid>
      <Grid>
        <Typography id="important-links-heading" variant="h6" component="h2" className={classes.documentsTitle}>
          {loginContent?.documents?.title}
        </Typography>
        <Box className={classes.documentsList} component="ul" aria-labelledby="important-links-heading">
          {loginContent?.documents?.links?.map((doc) => (
            <Typography
              key={`${doc.url ?? ''}-${doc.label ?? 'document'}`}
              variant="body1"
              component="li"
              className={classes.documentItem}
            >
              {doc.url && (
                <Link
                  href={doc.url}
                  {...(doc.label?.includes('Word')
                    ? {download: doc.label}
                    : {target: '_blank', rel: 'noopener noreferrer'})}
                  className={classes.documentLink}
                  aria-label={doc.label || 'Open document'}
                >
                  <strong>{doc.label}</strong>
                </Link>
              )}
            </Typography>
          ))}
        </Box>
      </Grid>
    </Stack>
  );
}
